import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiPencilOutline } from '@mdi/js'
import { useCan, useCanOrgWide } from '../../contexts/PermissionsContext'
import { KNOWLEDGE_KINDS, useNote, useUpdateAssetNote, useUpdateOrganizationNote, type KnowledgeKind } from '../../hooks/useKnowledge'
import { SECTION_ICONS } from '../../lib/section-icons'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { KindBadge } from './KindBadge'
import { NoteForm } from './NoteForm'
import { KnowledgeProposalDialog, type ProposalSeed } from './KnowledgeProposalDialog'

/** Where a knowledge fact's note lives: `knowledge/<n>` or `assets/<a>/notes/<n>`. */
export type KnowledgeSource = { noteUid: string; assetUid?: string }

export function parseKnowledgeSource(source: string): KnowledgeSource | null {
    const parts = source.split('/')
    if (parts.length === 2 && parts[0] === 'knowledge' && parts[1]) return { noteUid: parts[1] }
    if (parts.length === 4 && parts[0] === 'assets' && parts[2] === 'notes' && parts[1] && parts[3]) {
        return { assetUid: parts[1], noteUid: parts[3] }
    }

    return null
}

/** A knowledge fact's text is `[kind] Title: body`. */
export function parseKnowledgeText(text: string): { kind?: KnowledgeKind; title: string; body: string } {
    const m = /^\[(\w+)\]\s*([^:]*?):\s*([\s\S]*)$/.exec(text)
    if (!m) return { title: text, body: '' }
    const kind = KNOWLEDGE_KINDS.includes(m[1] as KnowledgeKind) ? (m[1] as KnowledgeKind) : undefined

    return { kind, title: m[2].trim(), body: m[3].trim() }
}

/**
 * A cited knowledge note on the triage card (spec 2026-11-01-01): the knowledge
 * icon and kind, linking to the note highlighted where it lives.
 */
export function KnowledgeFactLink({ org, source, text }: { org: string; source: KnowledgeSource; text: string }) {
    const { kind, title, body } = parseKnowledgeText(text)
    const className = 'flex min-w-0 items-center gap-1 text-foreground hover:underline'
    const content = (
        <>
            <AppIcon path={SECTION_ICONS.knowledge} size="xs" className="shrink-0" aria-hidden />
            {kind && <KindBadge kind={kind} />}
            <span className="truncate">{body ? `${title}: ${body}` : title}</span>
        </>
    )

    return source.assetUid ? (
        <Link
            to="/$org/assets/$assetUid"
            params={{ org, assetUid: source.assetUid }}
            search={{ tab: 'activity', note: source.noteUid }}
            className={className}
            data-testid="triage-fact-note-link"
        >
            {content}
        </Link>
    ) : (
        <Link to="/$org/knowledge" params={{ org }} search={{ note: source.noteUid }} className={className} data-testid="triage-fact-note-link">
            {content}
        </Link>
    )
}

/**
 * *This note is wrong* beside a cited note: a writer edits the note in place,
 * anyone else files an `update` proposal of it (origin `triage_card`).
 */
export function NoteWrongButton({
    org,
    source,
    factId,
    text,
}: {
    org: string
    source: KnowledgeSource
    factId: string
    text: string
}) {
    const { t } = useTranslation('knowledge')
    const canWriteAsset = useCan('knowledge.write')
    const canWriteOrg = useCanOrgWide('knowledge.write')
    const canWrite = source.assetUid ? canWriteAsset : canWriteOrg
    const [open, setOpen] = useState(false)
    const { data: note } = useNote(org, source.assetUid, source.noteUid, open && canWrite)
    const updateAsset = useUpdateAssetNote(org, source.assetUid ?? '')
    const updateOrg = useUpdateOrganizationNote(org)
    const parsed = parseKnowledgeText(text)
    const [seed, setSeed] = useState<ProposalSeed | null>(null)

    const start = () => {
        if (!canWrite) {
            setSeed({
                action: 'update',
                page_uid: source.noteUid,
                fact_id: factId,
                kind: parsed.kind,
                title: parsed.title,
                content: parsed.body,
                current_text: parsed.body,
            })
        }
        setOpen(true)
    }

    return (
        <>
            <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 shrink-0"
                onClick={start}
                aria-label={t('fact.wrong')}
                title={t('fact.wrong')}
                data-testid="triage-fact-note-wrong"
            >
                <AppIcon path={mdiPencilOutline} size="xs" />
            </Button>
            {canWrite ? (
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="triage-fact-note-dialog">
                        <DialogHeader>
                            <DialogTitle>{t('fact.edit_title')}</DialogTitle>
                        </DialogHeader>
                        {note ? (
                            <NoteForm
                                note={note}
                                withSubtreeOption={!!source.assetUid}
                                testIdPrefix="fact-note"
                                onCancel={() => setOpen(false)}
                                onSubmit={async (input) => {
                                    if (source.assetUid) await updateAsset.mutateAsync({ noteUid: source.noteUid, ...input })
                                    else await updateOrg.mutateAsync({ noteUid: source.noteUid, ...input })
                                    setOpen(false)
                                }}
                            />
                        ) : (
                            <p className="text-sm text-muted-foreground">{t('loading')}</p>
                        )}
                    </DialogContent>
                </Dialog>
            ) : (
                <KnowledgeProposalDialog org={org} open={open} onOpenChange={setOpen} seed={seed} targets={[]} />
            )}
        </>
    )
}
